import type { CSSProperties } from 'react'

import BarFill from '@/components/charts/BarFill'
import { cn } from '@/lib/utils'

/** Five equal bands: the cut points between them, on the 0-100 scale. */
const EQUAL_CUTS: readonly number[] = [20, 40, 60, 80]

interface MoodGaugeProps {
  /** 0-100, already clamped by the schema that parsed it. */
  score: number
  /** The backend's word for the reading — spoken, not drawn, by this element. */
  label: string
  /**
   * Whose reading it is, spoken first. The Stock page draws one company's
   * reading on this same track, and calling that "Market mood" would tell a
   * screen reader it is the market's.
   */
  subject?: string
  /**
   * Where the four cuts between the five bands fall, so a score lands under
   * the band its own model names. The market reading passes mm-be's
   * (`MOOD_CUTS`: 25 / 45 / 60 / 80), which are not fifths. Default: five
   * equal bands.
   */
  cuts?: readonly number[]
  className?: string
}

/**
 * The fear-and-greed track: five bands, and a marker where the score is.
 *
 * ── Five bands, not four ───────────────────────────────────────────────────
 *
 * The owner's rail image for `/market/*` (2026-09-29, Issue 7) draws five
 * bands — bright red, dim red, grey, dim green, bright green, left to right —
 * as five bands with small gaps between them and square corners. The earlier
 * V2 render this file used to cite drew four continuous quarters in a
 * rounded-full pill with no gaps; this reading REPLACES that shape rather than
 * adding a fifth band beside it, so there is no longer a single Figma node
 * both this comment and the render agree on — the image is the spec here, not
 * a frame id.
 *
 * The image draws them equal, and the company reading (five equal fifths)
 * keeps that. The market reading's bands are not equal — mm-be cuts its score
 * at 25 / 45 / 60 / 80 — so it hands in `cuts` and each band is as wide as
 * its share of the scale. That keeps the knob, which sits at `score`% of the
 * track, under the band mm-be named for it.
 *
 * The bands are still a SCALE, not a reading — the reading is the knob, the
 * label beside it and the "25 out of 100". The knob sits at `score`% of the
 * full track continuously; it is never snapped to a bucket's edge, so two
 * scores in the same bucket (28 and 42, both "cautious") land at two
 * different points along the same dim-red band, exactly as they did on the
 * four-band track.
 *
 * ── The colours ─────────────────────────────────────────────────────────────
 *
 * Two alpha steps of the same two market tokens the four-band track already
 * used, not a third colour: `--market-down-fill` at full strength for the
 * bright-red end and at `/55` for the dim-red band beside it, mirrored by
 * `--market-up-fill` on the green side; grey stays `--border-strong`,
 * unchanged, in the middle.
 *
 *   fear      --market-down-fill        bright red
 *   cautious  --market-down-fill/55     dim red
 *   neutral   --border-strong           grey
 *   warm      --market-up-fill/55       dim green
 *   greed     --market-up-fill          bright green
 *
 * `/55` is not a new number — it is this file's own old alpha for the WHOLE
 * fear (or greed) half under the four-band scheme, reused here as the DIM
 * step rather than re-derived, since nothing about the underlying tokens
 * changed, only how many bands the same ramp is now cut into. `--warning-fill`
 * is gone from this file: it stood in for the old scheme's amber "caution"
 * band (see tokens.semantic.css's own note on why it was added), and the
 * five-band image draws no amber anywhere — every band on it is a red, a
 * grey or a green.
 *
 * Using the market pair for a mood scale is still a deliberate exception to
 * the "market colour means direction and nothing else" rule in
 * tokens.semantic.css, and still the lesser of the two evils available. The
 * non-directional alternatives are `--destructive` (which resolves to the
 * SAME primitive, `--down-base`, so it is not even a different colour) and
 * `--success` — and both mean "an operation failed / succeeded", which is a
 * worse thing to say about a sentiment index than "this end is the red end".
 * The market pair also brings the one property a red-to-green ramp genuinely
 * needs: every band swaps to blue/orange under `:root.cb` — both alpha steps
 * included, since both derive from the same two tokens — so a deuteranope
 * can still read this track where `--destructive`/`--success` would have
 * stayed unreadable.
 *
 * What the system is actually missing is a named, non-directional sentiment
 * ramp in Layer 2. Reported rather than added: `src/styles/` is outside this
 * wave's file ownership.
 *
 * ── Colour never carries the meaning ──────────────────────────────────────
 *
 * The whole control is one `role="img"` with the label and the score in its
 * name, and the caller prints both as text beside it. In greyscale the knob's
 * position still reads, and with no colour vision at all the sentence does.
 */
export default function MoodGauge({
  score,
  label,
  subject = 'Market mood',
  cuts = EQUAL_CUTS,
  className,
}: MoodGaugeProps) {
  // Each band is as wide as its share of the 0-100 scale; `flex-1` keeps the zero basis.
  const edges = [0, ...cuts, 100]
  const share = (index: number): CSSProperties => ({
    flexGrow: (edges[index + 1] ?? 100) - (edges[index] ?? 0),
  })

  return (
    <div
      role="img"
      aria-label={`${subject}: ${label}, ${String(Math.round(score))} out of 100`}
      // 10 → h-2 (4-grid, nearest-8 tie-break; the render measures 10px).
      className={cn('relative h-2 w-full', className)}
    >
      {/* `gap-1`: the image's own small seam between bands. Square, not the
          old rounded-full pill — no rounding class on this row or a band. */}
      <div className="flex size-full gap-1">
        <div className="flex-1 bg-market-down-fill" style={share(0)} />
        <div className="flex-1 bg-market-down-fill/55" style={share(1)} />
        <div className="flex-1 bg-border-strong" style={share(2)} />
        <div className="flex-1 bg-market-up-fill/55" style={share(3)} />
        <div className="flex-1 bg-market-up-fill" style={share(4)} />
      </div>
      {/* A thin bar, not the four-band version's ringed circle — the image
          draws a hairline, not a dot. `bg-foreground` is white in dark and
          near-black in light, the highest-contrast mark on the track either
          way, and needs no `border-card` ring to read as lifted off a track
          it already stands taller than (`h-3` over the track's `h-2`). */}
      {/* The marker rides a full-width layer that grows from the left, so it
          slides from the start of the track to the score on mount. */}
      <BarFill className="absolute inset-0">
        <span
          className="absolute top-1/2 h-3 w-1 -translate-x-1/2 -translate-y-1/2 bg-foreground"
          style={{ left: `${String(score)}%` }}
        />
      </BarFill>
    </div>
  )
}
